import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Button,
  Card,
  Row,
  Col,
  ButtonGroup,
  Image,
  Spinner,
  Form,
  InputGroup,
  Alert,
  Badge,
} from "react-bootstrap";
import { getDownloadURL, listAll, ref } from "firebase/storage";
import { storage } from "../../firebase";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faChevronLeft,
  faChevronRight,
  faFolderOpen,
} from "@fortawesome/free-solid-svg-icons";

export default function File() {
  const location = useLocation();
  const fullPath = location?.state?.fullPath ?? "";
  const listRef = useMemo(
    () => (fullPath ? ref(storage, fullPath) : null),
    [fullPath]
  );
  const [files, setFiles] = useState([]);
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState(true);
  const [error, setError] = useState("");
  const [jumpPage, setJumpPage] = useState("");
  const [jumpError, setJumpError] = useState("");
  const [securityNotice, setSecurityNotice] = useState("");
  const viewerRef = useRef(null);

  useEffect(() => {
    if (!listRef) {
      setFiles([]);
      setLoading(false);
      return;
    }

    async function fetchFiles() {
      setLoading(true);
      setImageLoading(true);
      setError("");

      try {
        const res = await listAll(listRef);
        const filePromises = res.items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef);
          return {
            name: itemRef.name,
            url,
            reference: itemRef,
          };
        });
        const loadedFiles = await Promise.all(filePromises);
        loadedFiles.sort((a, b) => a.name.localeCompare(b.name));
        setFiles(loadedFiles);
        setCurrentIndex(0);
        setImageLoading(true);
      } catch (err) {
        setError(
          "Unable to load files for this collection. Please try again later."
        );
        setFiles([]);
      } finally {
        setLoading(false);
      }
    }
    fetchFiles();
  }, [listRef]);

  const totalFiles = files.length;
  const currentFile = totalFiles > 0 ? files[currentIndex] : null;
  const folderName = useMemo(() => {
    if (!fullPath) return "Collection";
    const parts = fullPath.split("/");
    return parts[parts.length - 1] || fullPath;
  }, [fullPath]);

  function handlePrev() {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  }

  function handleNext() {
    setCurrentIndex((prev) => Math.min(prev + 1, totalFiles - 1));
  }

  function handleBack() {
    navigate(-1);
  }

  // Keyboard navigation
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.ctrlKey || e.metaKey) && ["s", "p", "S", "P"].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        setSecurityNotice(
          "Saving or printing is disabled for protected documents."
        );
        return;
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        e.shiftKey &&
        ["i", "I", "s", "S"].includes(e.key)
      ) {
        e.preventDefault();
        e.stopPropagation();
        setSecurityNotice("Developer tools are restricted for this viewer.");
        return;
      }
      if (e.key === "PrintScreen") {
        e.preventDefault();
        e.stopPropagation();
        setSecurityNotice("Screenshots are disabled for this viewer.");
        return;
      }
      if (e.key === "ArrowLeft" && currentIndex > 0) {
        setCurrentIndex((prev) => Math.max(prev - 1, 0));
      }
      if (e.key === "ArrowRight" && currentIndex < totalFiles - 1) {
        setCurrentIndex((prev) => Math.min(prev + 1, totalFiles - 1));
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, totalFiles]);

  useEffect(() => {
    const element = viewerRef.current;
    if (!element) return undefined;

    const handleContextMenu = (event) => {
      event.preventDefault();
      setSecurityNotice(
        "Right-click has been disabled to protect these documents."
      );
    };

    element.addEventListener("contextmenu", handleContextMenu);
    const handleDragStart = (event) => {
      event.preventDefault();
      setSecurityNotice(
        "Downloading or copying is restricted for this viewer."
      );
    };
    element.addEventListener("dragstart", handleDragStart);

    return () => {
      element.removeEventListener("contextmenu", handleContextMenu);
      element.removeEventListener("dragstart", handleDragStart);
    };
  }, [loading, totalFiles]);

  useEffect(() => {
    if (!securityNotice) return undefined;
    const timeout = setTimeout(() => setSecurityNotice(""), 4000);
    return () => clearTimeout(timeout);
  }, [securityNotice]);

  useEffect(() => {
    if (totalFiles > 0) {
      setImageLoading(true);
    }
  }, [currentIndex, totalFiles]);

  // Jump to page
  function handleJumpPage(e) {
    e.preventDefault();
    const pageNum = parseInt(jumpPage, 10);
    if (Number.isNaN(pageNum)) {
      setJumpError("Enter a valid page number.");
      return;
    }
    if (pageNum < 1 || pageNum > totalFiles) {
      setJumpError(`Page must be between 1 and ${totalFiles || 1}.`);
      return;
    }
    setCurrentIndex(pageNum - 1);
    setJumpPage("");
    setJumpError("");
  }

  if (!fullPath) {
    return (
      <Container fluid className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center p-5">
                <h2 className="h4 mb-3">No collection selected</h2>
                <p className="text-muted mb-4">
                  Head back to the Viewable Files directory and pick a folder to
                  begin browsing files.
                </p>
                <Button
                  variant="primary"
                  onClick={() => navigate("/viewFiles")}
                >
                  Go to Viewable Files
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="py-5 file-viewer-page">
      <Row className="justify-content-center">
        <Col xl={10} lg={11}>
          <Card className="border-0 shadow-sm text-white bg-primary mb-4">
            <Card.Body>
              <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                <div>
                  <Badge bg="light" text="dark" className="text-uppercase mb-2">
                    Collection viewer
                  </Badge>
                  <h1 className="display-6 fw-semibold mb-1">{folderName}</h1>
                  <p className="mb-0">
                    Use the navigation controls or arrow keys to flip through
                    each page in this collection.
                  </p>
                </div>
                <div className="text-lg-end">
                  <p className="text-uppercase small text-white-50 mb-1">
                    Total files
                  </p>
                  <h2 className="fw-bold mb-0">
                    <FontAwesomeIcon icon={faFolderOpen} className="me-2" />
                    {totalFiles}
                  </h2>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-4 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
              <ButtonGroup className="flex-wrap">
                <Button
                  variant="outline-primary"
                  className="rounded-pill px-3"
                  onClick={handleBack}
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                  Back to collections
                </Button>
                <Button
                  variant="outline-secondary"
                  className="rounded-pill px-3"
                  onClick={handlePrev}
                  disabled={currentIndex === 0 || totalFiles === 0}
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
                  Previous
                </Button>
                <Button
                  variant="outline-secondary"
                  className="rounded-pill px-3"
                  onClick={handleNext}
                  disabled={currentIndex >= totalFiles - 1 || totalFiles === 0}
                >
                  Next
                  <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
                </Button>
              </ButtonGroup>

              <Form onSubmit={handleJumpPage} className="w-auto">
                <InputGroup>
                  <InputGroup.Text>Jump to page</InputGroup.Text>
                  <Form.Control
                    type="number"
                    min={1}
                    max={totalFiles || 1}
                    value={jumpPage}
                    onChange={(event) => setJumpPage(event.target.value)}
                    disabled={totalFiles === 0}
                    style={{ width: "120px" }}
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={totalFiles === 0}
                  >
                    Go
                  </Button>
                </InputGroup>
              </Form>
            </Card.Body>
          </Card>

          {(jumpError || securityNotice) && (
            <Alert variant="warning" className="mb-4">
              {jumpError || securityNotice}
            </Alert>
          )}
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}

          <Card
            ref={viewerRef}
            className="border-0 shadow-sm"
            style={{ userSelect: "none" }}
          >
            <Card.Body className="p-4">
              {loading ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: "40vh" }}
                >
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : totalFiles === 0 ? (
                <div className="py-5 text-center text-muted">
                  <h3 className="h5 mb-2">No files available</h3>
                  <p className="mb-0">
                    Once files are uploaded to this collection, they will appear
                    here for viewing.
                  </p>
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <div>
                      <h2 className="h5 mb-0 text-capitalize">
                        {currentFile?.name}
                      </h2>
                      <p className="text-muted mb-0 small">
                        Page {currentIndex + 1} of {totalFiles}
                      </p>
                    </div>
                    <Badge bg="primary" className="rounded-pill px-3 py-2">
                      {currentIndex + 1} / {totalFiles}
                    </Badge>
                  </div>

                  <div
                    className="bg-body-secondary rounded-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: "55vh" }}
                  >
                    {imageLoading && (
                      <Spinner animation="border" variant="primary" />
                    )}
                    {currentFile?.url && (
                      <Image
                        id="ViewFilesImage"
                        src={currentFile.url}
                        alt={currentFile.name}
                        fluid
                        className="rounded-4 shadow-sm"
                        draggable={false}
                        onLoad={() => setImageLoading(false)}
                        onError={() => setImageLoading(false)}
                        style={{
                          maxHeight: "60vh",
                          objectFit: "contain",
                        }}
                      />
                    )}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
